"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cpu, Cloud } from "lucide-react";
function NeuralNetworkAnimation() {
  const nodes = Array.from({
    length: 12
  }).map((_, i) => ({
    x: (Math.random() - 0.5) * 80,
    y: (Math.random() - 0.5) * 80,
    delay: Math.random() * 2
  }));
  return <div className="absolute inset-0 overflow-hidden">
      <svg className="w-full h-full" viewBox="-50 -50 100 100" preserveAspectRatio="xMidYMid slice">
        {/* Connection lines */}
        {nodes.map((node, i) => nodes.slice(i + 1).map((otherNode, j) => Math.random() > 0.7 ? <motion.line key={`line-${i}-${j}`} x1={node.x} y1={node.y} x2={otherNode.x} y2={otherNode.y} stroke="#3b82f6" strokeWidth="0.3" strokeOpacity="0.3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 2,
        delay: node.delay,
        repeat: Infinity,
        repeatType: "reverse",
        repeatDelay: 1
      }} /> : null))}
        
        {/* Nodes */}
        {nodes.map((node, i) => <motion.circle key={`node-${i}`} cx={node.x} cy={node.y} r="1.5" fill="#60a5fa" initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: [1, 1.2, 1],
        opacity: [0.8, 1, 0.8]
      }} transition={{
        duration: 3,
        delay: node.delay,
        repeat: Infinity,
        repeatType: "reverse"
      }} style={{
        filter: "drop-shadow(0 0 4px #2563eb)"
      }} />)}
      </svg>
    </div>;
}
function ServerNodeAnimation() {
  return <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
      <svg className="w-64 h-64" viewBox="0 0 200 200">
        {/* Main server box */}
        <motion.rect x="50" y="30" width="100" height="140" fill="#0f172a" stroke="#06b6d4" strokeWidth="2" rx="4" animate={{
        rotateY: [0, 360]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }} style={{
        transformOrigin: "100px 100px"
      }} />
        
        {/* Server indicator lights */}
        {[-50, -30, -10, 10, 30].map((y, i) => <motion.rect key={`light-${i}`} x="60" y={100 + y} width="80" height="4" fill="#22d3ee" rx="2" initial={{
        opacity: 0.3
      }} animate={{
        opacity: [0.3, 1, 0.3]
      }} transition={{
        duration: 2,
        delay: i * 0.2,
        repeat: Infinity,
        repeatType: "reverse"
      }} style={{
        filter: "drop-shadow(0 0 8px #0891b2)"
      }} />)}

        {/* Orbiting nodes */}
        {[0, 1, 2].map(i => <motion.g key={`orbit-${i}`}>
            <motion.circle cx="100" cy="100" r="4" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0.3" animate={{
          r: [60, 70, 60]
        }} transition={{
          duration: 3,
          delay: i * 0.5,
          repeat: Infinity
        }} />
            <motion.rect x="160" y="96" width="8" height="8" fill="#0e7490" stroke="#06b6d4" strokeWidth="1" animate={{
          rotate: [i * 120, i * 120 + 360]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }} style={{
          transformOrigin: "100px 100px",
          filter: "drop-shadow(0 0 4px #06b6d4)"
        }} />
          </motion.g>)}
      </svg>
    </div>;
}
export default function TechSynergyAiCloud() {
  const [hoveredSide, setHoveredSide] = useState<"left" | "right" | null>(null);
  return <section id="tech-synergy-ai-cloud" className="dark bg-background text-foreground relative w-full min-h-[800px] flex flex-col md:flex-row overflow-hidden">
      {/* AI Consulting Side */}
      <div className="relative flex flex-col items-center justify-center overflow-hidden transition-all duration-700 ease-in-out border-b md:border-b-0 md:border-r border-white/10" style={{
      flex: hoveredSide === "left" ? 2 : hoveredSide === "right" ? 1 : 1
    }} onMouseEnter={() => setHoveredSide("left")} onMouseLeave={() => setHoveredSide(null)}>
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-slate-950 to-black z-0" />
        
        {/* Animated background */}
        <div className="absolute inset-0 z-10 opacity-60 md:opacity-80 pointer-events-none">
          <NeuralNetworkAnimation />
        </div>

        {/* Content */}
        <div className="relative z-20 p-8 md:p-12 text-center max-w-xl mx-auto flex flex-col items-center">
          <div className="mb-6 p-4 rounded-full bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm">
            <Cpu className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]">
            AI Consulting
          </h2>
          <p className="text-lg text-blue-100/80 mb-8 leading-relaxed max-w-md">
            Unlock intelligent automation and data-driven insights. We build custom AI models that optimize your business processes and drive innovation.
          </p>
          <div className={`transition-all duration-500 overflow-hidden ${hoveredSide === "left" ? "max-h-20 opacity-100" : "max-h-0 opacity-0 md:max-h-20 md:opacity-100"}`}>
             <Button asChild variant="default" className="bg-blue-600 hover:bg-blue-500 text-white border-none shadow-[0_0_20px_rgba(37,99,235,0.3)]">
              <Link to="/SolutionsPage">
                Explore AI Solutions <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Cloud Services Side */}
      <div className="relative flex flex-col items-center justify-center overflow-hidden transition-all duration-700 ease-in-out" style={{
      flex: hoveredSide === "right" ? 2 : hoveredSide === "left" ? 1 : 1
    }} onMouseEnter={() => setHoveredSide("right")} onMouseLeave={() => setHoveredSide(null)}>
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-bl from-cyan-950 via-slate-900 to-black z-0" />

        {/* Animated background */}
        <div className="absolute inset-0 z-10 opacity-60 md:opacity-80 pointer-events-none">
          <ServerNodeAnimation />
        </div>

        {/* Content */}
        <div className="relative z-20 p-8 md:p-12 text-center max-w-xl mx-auto flex flex-col items-center">
          <div className="mb-6 p-4 rounded-full bg-cyan-500/10 border border-cyan-500/20 backdrop-blur-sm">
            <Cloud className="w-8 h-8 text-cyan-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white drop-shadow-[0_0_15px_rgba(6,182,212,0.5)]">
            Cloud Services
          </h2>
          <p className="text-lg text-cyan-100/80 mb-8 leading-relaxed max-w-md">
            Scalable, secure, and efficient cloud infrastructure. Migrate and modernize your legacy systems for the digital age with our expert guidance.
          </p>
          <div className={`transition-all duration-500 overflow-hidden ${hoveredSide === "right" ? "max-h-20 opacity-100" : "max-h-0 opacity-0 md:max-h-20 md:opacity-100"}`}>
            <Button asChild variant="default" className="bg-cyan-600 hover:bg-cyan-500 text-white border-none shadow-[0_0_20px_rgba(8,145,178,0.3)]">
              <Link to="/SolutionsPage">
                Discover Cloud <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}